import React from "react";
import {
  Box,
  BoxProps,
  Theme,
  Typography,
  TypographyOwnProps,
  useTheme,
} from "@mui/material";
import { useMandatorContext } from "@bn/theming";

const getStyle = (theme: Theme, variant: BadgeProps["variant"]) => {
  switch (variant) {
    case "primary":
      return {
        backgroundColor: theme.palette.background.selected,
        color: theme.palette.primary.main,
      };
    case "secondary":
      return {
        backgroundColor: theme.palette.background.upgrade,
        color: theme.palette.text.upgrade,
      };
    case "accent":
      return {
        backgroundColor: "transparent",
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.lighter}`,
      };
    case "info":
      return {
        backgroundColor: theme.palette.background.info,
        color: theme.palette.info.main,
      };
    case "warning":
      return {
        backgroundColor: theme.palette.background.warning,
        color: theme.palette.text.warning,
      };
    case "success":
      return {
        backgroundColor: theme.palette.background.success,
        color: theme.palette.text.positiveOnBadge,
      };
    case "error":
      return {
        backgroundColor: theme.palette.background.error,
        color: theme.palette.text.negative,
      };
    case "default":
      return {
        backgroundColor: theme.palette.background.neutral,
        color: theme.palette.text.primary,
      };
  }
};

export type BadgeVariant =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "warning"
  | "info"
  | "accent";

export type BadgeProps = {
  variant?: BadgeVariant;
  textVariant?: TypographyOwnProps["variant"];
  children: React.ReactNode;
  sx?: BoxProps["sx"];
} & BoxProps;

export const Badge = ({
  variant = "default",
  textVariant = "body3",
  children,
  sx,
  ...rest
}: BadgeProps) => {
  const theme = useTheme();
  const { isAriva } = useMandatorContext();
  const styles = getStyle(theme, variant);

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: styles?.backgroundColor || "transparent",
        borderRadius: isAriva ? "100px" : `${theme.shape.borderRadius}px`,
        border: styles?.border || "none",
        cursor: rest.onClick ? "pointer" : "default",
        "&:hover": {
          opacity: rest.onClick ? 0.8 : 1,
        },
        ...sx,
      }}
      py={styles?.border ? 0.5 : 0.75}
      px={2}
      {...rest}
    >
      {typeof children === "string" ? (
        <Typography
          variant={textVariant}
          color={styles?.color || "textPrimary"}
        >
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
};
